/**
 * SettingsPage — runtime ops dials.
 *
 * Lists every catalog entry grouped by category. Each row shows the
 * current value (override or default), the spec's description, and
 * an inline editor. Save → PUT /api/admin/settings/:key. Reset →
 * DELETE /api/admin/settings/:key.
 *
 * Gated server-side on platform.settings.read / .write — only
 * platform_admin sees this page at all. The Nav link is hidden for
 * lesser roles via the same permission check pattern other pages use.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError } from "@dfp/api-client";
import { Button } from "../components/Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { CascadePolicyRow } from "../components/CascadePolicyEditor.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";

type SettingValue = string | number | boolean | readonly string[];

interface SettingRow {
  readonly key: string;
  readonly group:
    | "reconciliation"
    | "onboarding"
    | "notifications"
    | "safety"
    | "lock"
    | "enrollment"
    | "payments"
    | "app_update";
  readonly label: string;
  readonly description: string;
  readonly kind: "number_ms" | "number_days" | "number" | "boolean" | "string" | "string_set";
  /** Closed choice set for `string_set` (checkboxes) or single-select `string` (dropdown). */
  readonly choices: readonly string[] | null;
  readonly defaultValue: SettingValue;
  readonly value: SettingValue;
  readonly hasOverride: boolean;
  readonly updatedAt: string | null;
  readonly updatedBy: string | null;
}

const GROUP_LABEL: Record<SettingRow["group"], string> = {
  reconciliation: "Reconciliation thresholds",
  onboarding: "Onboarding traffic light",
  notifications: "Notification toggles",
  lock: "Lock grace periods",
  safety: "Safety overrides",
  enrollment: "DPC enrollment URLs",
  payments: "Payments",
  app_update: "DPC auto-update",
};
const GROUP_ORDER: SettingRow["group"][] = [
  "reconciliation",
  "onboarding",
  "enrollment",
  "app_update",
  "notifications",
  "lock",
  "payments",
  "safety",
];

export function SettingsPage(): JSX.Element {
  const auth = useAuth();
  const canWrite = (auth.user?.permissions ?? []).includes("platform.settings.write");

  const q = useQuery({
    queryKey: ["admin", "settings"],
    queryFn: () => api.get<readonly SettingRow[]>("/api/admin/settings"),
  });

  if (q.isLoading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (q.isError || !q.data) {
    return <p className="text-sm text-red-700">Failed to load settings.</p>;
  }

  // Group rows by their catalog group. Built defensively (string-keyed, lazy)
  // so a group the server adds but this page hasn't curated yet renders under
  // its raw key instead of crashing the whole page on an undefined bucket.
  const grouped: Record<string, SettingRow[]> = {};
  for (const row of q.data) (grouped[row.group] ??= []).push(row);

  // Curated order first, then any server groups not in GROUP_ORDER (forward-
  // compatible: a new catalog group still appears rather than vanishing).
  const orderedGroups: string[] = [
    ...GROUP_ORDER,
    ...Object.keys(grouped).filter((g) => !(GROUP_ORDER as readonly string[]).includes(g)),
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Settings</h1>
        <p className="text-sm text-slate-500">
          Runtime ops dials. Changes hot-reload across the platform within 30 seconds.
        </p>
      </div>
      {orderedGroups.map((group) => {
        const rows = grouped[group];
        if (rows === undefined || rows.length === 0) return null;
        return (
          <Card key={group}>
            <CardHeader>
              <CardTitle>{GROUP_LABEL[group as SettingRow["group"]] ?? group}</CardTitle>
            </CardHeader>
            <CardBody className="divide-y divide-slate-200">
              {rows.map((row) =>
                row.key === "lock.cascade_policy_set" ? (
                  <CascadePolicyRow key={row.key} row={row} canWrite={canWrite} />
                ) : (
                  <SettingRow key={row.key} row={row} canWrite={canWrite} />
                ),
              )}
            </CardBody>
          </Card>
        );
      })}
    </div>
  );
}

function SettingRow({ row, canWrite }: { row: SettingRow; canWrite: boolean }): JSX.Element {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(formatRawForInput(row));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (value: unknown) =>
      api.put<{ key: string; value: unknown }>(`/api/admin/settings/${row.key}`, { value }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "settings"] });
      setEditing(false);
      setError(null);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setError(err.problem.message ?? err.problem.detail ?? "Save failed");
      } else {
        setError("Network error");
      }
    },
  });
  const reset = useMutation({
    mutationFn: () => api.delete<{ key: string }>(`/api/admin/settings/${row.key}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "settings"] });
    },
  });

  function attemptSave(): void {
    setError(null);
    const parsed = parseDraftForRow(row, draft);
    if (parsed.ok) save.mutate(parsed.value);
    else setError(parsed.reason);
  }

  return (
    <div className="space-y-2 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900">{row.label}</p>
          <p className="font-mono text-[10px] text-slate-500">{row.key}</p>
        </div>
        <div className="flex items-center gap-2">
          {row.hasOverride ? (
            <span className="rounded bg-amber-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800">
              Override
            </span>
          ) : (
            <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600">
              Default
            </span>
          )}
        </div>
      </div>
      <p className="text-xs text-slate-600">{row.description}</p>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {!editing ? (
          <>
            <span className="rounded border border-slate-200 bg-slate-50 px-2 py-1 font-mono">
              {formatValue(row, row.value)}
            </span>
            {canWrite ? (
              <Button
                variant="secondary"
                onClick={() => {
                  setDraft(formatRawForInput(row));
                  setEditing(true);
                }}
              >
                Edit
              </Button>
            ) : null}
            {canWrite && row.hasOverride ? (
              <Button variant="secondary" onClick={() => reset.mutate()}>
                Reset
              </Button>
            ) : null}
            {row.updatedAt ? (
              <span className="text-slate-500">
                · changed {new Date(row.updatedAt).toLocaleString()}
              </span>
            ) : null}
          </>
        ) : (
          <>
            {row.kind === "boolean" ? (
              <select
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="rounded-md border border-slate-300 px-2 py-1"
              >
                <option value="true">true</option>
                <option value="false">false</option>
              </select>
            ) : row.kind === "string_set" && row.choices ? (
              <div className="flex flex-wrap gap-2">
                {row.choices.map((choice) => {
                  const selected = parseDraftAsSet(draft).includes(choice);
                  return (
                    <label
                      key={choice}
                      className={`flex cursor-pointer items-center gap-1 rounded-md border px-2 py-1 font-mono text-[11px] ${
                        selected
                          ? "border-emerald-400 bg-emerald-50 text-emerald-900"
                          : "border-slate-300 bg-white text-slate-600"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={(e) =>
                          setDraft(toggleInSetDraft(draft, choice, e.target.checked))
                        }
                      />
                      {choice}
                    </label>
                  );
                })}
              </div>
            ) : row.kind === "string_set" ? (
              // Free-form string list (no closed choices). Used for
              // ops-typed allowlists like FRP recovery emails. Render
              // as a comma-separated text input — the server's
              // catalog coerces + validates each item, so an invalid
              // entry comes back as the save error rather than being
              // silently dropped.
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="comma-separated, e.g. ops@example.com, backup@example.com"
                className="rounded-md border border-slate-300 px-2 py-1 font-mono text-xs w-full max-w-xl"
              />
            ) : row.kind === "string" && row.choices ? (
              // Single-select string with a closed choice set (e.g. the
              // default payment provider). Dropdown of the available
              // options. The current value is added if it's not in the
              // list so a legacy / custom value still shows.
              <select
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="rounded-md border border-slate-300 px-2 py-1 font-mono text-xs"
              >
                {(row.choices.includes(draft) ? row.choices : [draft, ...row.choices]).map(
                  (choice) => (
                    <option key={choice} value={choice}>
                      {choice}
                    </option>
                  ),
                )}
              </select>
            ) : row.kind === "string" ? (
              // Single free-form string (e.g. DPC API base URL). The
              // server's catalog coerce validates shape (URL parser,
              // protocol restriction). Empty input is legal: it
              // signals "use env-var / compile-time fallback" without
              // requiring a DELETE round-trip.
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="e.g. https://dpc-api.simuflex.tz"
                className="rounded-md border border-slate-300 px-2 py-1 font-mono text-xs w-full max-w-xl"
              />
            ) : (
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                inputMode="numeric"
                className="rounded-md border border-slate-300 px-2 py-1 font-mono"
              />
            )}
            <Button onClick={attemptSave} disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setEditing(false);
                setError(null);
              }}
            >
              Cancel
            </Button>
            <span className="text-slate-500">Default: {formatValue(row, row.defaultValue)}</span>
          </>
        )}
        {error ? <span className="text-red-700">{error}</span> : null}
      </div>
    </div>
  );
}

/* ─── helpers ──────────────────────────────────────────────────────── */

function formatValue(row: SettingRow, v: SettingValue): string {
  if (Array.isArray(v)) {
    return v.length === 0 ? "(none)" : v.join(", ");
  }
  if (typeof v === "boolean") return v ? "ON" : "OFF";
  if (typeof v === "string") {
    // Empty string = "use fallback" for the DPC URL settings. Render
    // that as a hint, not blank space the operator can't see.
    if (row.kind === "string" && v === "") return "(env / default fallback)";
    return v;
  }
  if (typeof v !== "number") return String(v);
  if (row.kind === "number_ms") return prettyMs(v);
  return String(v);
}

function prettyMs(ms: number): string {
  const s = ms / 1000;
  const m = s / 60;
  const h = m / 60;
  const d = h / 24;
  if (d >= 1) return `${trim(d)} d (${ms} ms)`;
  if (h >= 1) return `${trim(h)} h (${ms} ms)`;
  if (m >= 1) return `${trim(m)} min (${ms} ms)`;
  return `${trim(s)} s (${ms} ms)`;
}
function trim(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

function formatRawForInput(row: SettingRow): string {
  if (row.kind === "string_set" && Array.isArray(row.value)) {
    // Closed-choice sets round-trip via JSON so the toggle helpers
    // can parse without ambiguity. Free-form lists (no `choices`)
    // render as CSV — operators type their entries directly into
    // the input, and a CSV is more readable + easier to edit than
    // a quoted JSON array on a typical settings row.
    if (row.choices) return JSON.stringify(row.value);
    return row.value.join(", ");
  }
  // String kind: hand the raw string through. Avoids `String(value)`
  // turning a non-string by mistake — the catalog coerce guarantees
  // string values for string-kind rows.
  if (row.kind === "string") return typeof row.value === "string" ? row.value : "";
  return String(row.value);
}

function parseDraftAsSet(draft: string): readonly string[] {
  try {
    const parsed = JSON.parse(draft.trim() === "" ? "[]" : draft);
    if (Array.isArray(parsed)) {
      return parsed.filter((s): s is string => typeof s === "string");
    }
  } catch {
    // Fall through to CSV legacy parser
  }
  return draft
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function toggleInSetDraft(draft: string, choice: string, on: boolean): string {
  const set = new Set(parseDraftAsSet(draft));
  if (on) set.add(choice);
  else set.delete(choice);
  return JSON.stringify([...set].sort());
}

function parseDraftForRow(
  row: SettingRow,
  draft: string,
): { ok: true; value: SettingValue } | { ok: false; reason: string } {
  const trimmed = draft.trim();
  if (row.kind === "boolean") {
    if (trimmed === "true") return { ok: true, value: true };
    if (trimmed === "false") return { ok: true, value: false };
    return { ok: false, reason: "Value must be true or false." };
  }
  if (row.kind === "string_set") {
    const set = parseDraftAsSet(trimmed);
    if (row.choices) {
      // Closed choice set — reject anything outside the allowed list.
      const allowed = new Set(row.choices);
      for (const v of set) {
        if (!allowed.has(v)) return { ok: false, reason: `Unknown value: ${v}` };
      }
      return { ok: true, value: set };
    }
    // Free-form list (e.g. FRP recovery emails). Per-item validity
    // is enforced by the server's catalog coercer, so we hand the
    // array off without further checks — an invalid email comes
    // back as a "Value for X failed validation" 400 from /api/settings.
    return { ok: true, value: set };
  }
  if (row.kind === "string") {
    // Hand the trimmed string to the server — its coerce decides
    // whether the shape is acceptable (URL parser, protocol check,
    // etc). Empty string is legal: it means "use the fallback rung
    // (env var / compile-time default)" without a separate reset
    // round-trip.
    return { ok: true, value: trimmed };
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return { ok: false, reason: "Value must be a number." };
  if (row.kind === "number_ms" || row.kind === "number") {
    if (!Number.isInteger(n)) return { ok: false, reason: "Value must be an integer." };
    if (n <= 0) return { ok: false, reason: "Value must be > 0." };
  }
  return { ok: true, value: n };
}
